/** An empty state that says how to populate the thing, not just that it is empty.
 *
 * An empty list is usually a fact the reader can act on ("no documents yet: add one"), so the
 * component asks for the how-to text and an optional action. It is not an error: a failed read is
 * `ScreenFailed` / a table's `error` slot, and must never render as this (a failed read is not an
 * empty one).
 */

import { Tray } from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import styles from "./EmptyState.module.css";

export function EmptyState({
  title,
  children,
  action,
  icon: Glyph = Tray,
  compact = false,
  testId = "empty-state",
}: {
  title: string;
  /** How to populate it, or why it is empty. */
  children?: ReactNode;
  action?: ReactNode;
  icon?: Icon;
  /** Less padding, for inside a table or a small panel. */
  compact?: boolean;
  testId?: string;
}) {
  return (
    <div className={`${styles.empty} ${compact ? styles.compact : ""}`} data-testid={testId}>
      <span className={styles.badge} aria-hidden="true">
        <Glyph size={compact ? 20 : 26} weight="duotone" />
      </span>
      {/* A heading, not a paragraph: the headline names the space ("No bills yet") and is not one of the
          page's sentences, so the text budget counts the one short sentence under it and not two. */}
      <h2 className={styles.title}>{title}</h2>
      {children ? <div className={styles.body}>{children}</div> : null}
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
